import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useCallback } from "react";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { currentProfileAtom } from "../../../features/Club/atoms";
import {
  WhooshMutationError,
  useMutationWithError,
} from "../../../utils/hooks/useMutationWithError";
import {
  AddEventSignUpToConfirmedInput,
  useAddEventSignUpToConfirmedMutation,
  useAddEventSignUpToConfirmedMutation$data,
} from "./__generated__/useAddEventSignUpToConfirmedMutation.graphql";

export const useAddEventSignUpToConfirmed = (
  successCallback?: (signUpId?: string | null) => void
) => {
  const { showToast } = useToast();

  const currentProfile = useAtomValue(currentProfileAtom);
  const memberId = currentProfile?.memberId;

  const [addEventRegistration, isAdding] =
    useMutationWithError<useAddEventSignUpToConfirmedMutation>(
      useAddEventSignUpToConfirmedMutationString
    );

  const onCompleted = useCallback(
    (res: useAddEventSignUpToConfirmedMutation$data, isDraft?: boolean) => {
      if (res.responsePayload?.success) {
        if (!isDraft) {
          showToast({
            description: t`Registration Successfully Created.`,
            semanticStyle: "success",
            toastId: "create-registration-success",
          });
        }
        successCallback?.(res.responsePayload.event?.signUpForClient?.id);
      }
    },
    [showToast, successCallback]
  );

  const onCompletedErrorCustom = useCallback(
    (errors: WhooshMutationError[]) => {
      /**
       * Handle trying to sign up a member that already exists.
       */
      if (errors.find((e) => e.code === "already_taken")) {
        showToast({
          description: t`One of your group members is already signed up for the event. Please remove them from your group and try again.`,
          toastId: "create-registration-error-already-taken",
          semanticStyle: "error",
        });
      } else {
        showToast({
          description: t`Sorry. An error occurred while creating your registration.`,
          toastId: "create-registration-error",
          semanticStyle: "error",
        });
      }

      return [];
    },
    [showToast]
  );

  const handleAddEventSignUpToConfirmed = useCallback(
    (input: AddEventSignUpToConfirmedInput) => {
      const isDraft = input.eventSignUp?.isDraft === true;

      if (memberId)
        addEventRegistration({
          variables: { input, memberId },
          onCompleted: (response: useAddEventSignUpToConfirmedMutation$data) =>
            onCompleted(response, isDraft),
          onCompletedErrorCustom,
        });
    },
    [addEventRegistration, memberId, onCompleted, onCompletedErrorCustom]
  );

  return [handleAddEventSignUpToConfirmed, isAdding] as const;
};

const useAddEventSignUpToConfirmedMutationString = graphql`
  mutation useAddEventSignUpToConfirmedMutation(
    $input: AddEventSignUpToConfirmedInput!
    $memberId: ID!
  ) {
    responsePayload: addEventSignUpToConfirmed(input: $input) {
      errors {
        field
        code
        message
      }
      success
      event {
        id
        eventSeries {
          events {
            edges {
              node {
                accessStatusForClient(clientId: $memberId)
                signUpStatusForClient(clientId: $memberId)
              }
            }
          }
        }
        signUpForClient(clientId: $memberId) {
          id
        }
        ...EventDetailAttendeesList
        ...useInitEventSignUps_event
        ...EventDetailGroupDisplay_event
      }
    }
  }
`;
